import { LanguageFilters } from '@/components/home/language-filters';
import { TemplateButton } from '@/components/home/template-button';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useIsMobile } from '@/hooks/use-mobile';
import { useSettings } from '@/hooks/use-settings';
import { SUPPORTED_LOCALES } from '@/i18n/i18n';
import { convexQuery, useConvexMutation } from '@convex-dev/react-query';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { Id } from 'convex/_generated/dataModel';
import { ChevronDown, Dot, Loader2 } from 'lucide-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '~/convex/_generated/api';
import type { Template } from '~/convex/schema';

interface TemplatePickerProps {
  selectedTemplateId: Id<'templates'> | null;
  onSelectTemplate: (id: Id<'templates'>) => void;
  disabled?: boolean;
}

export const TemplatePicker = ({
  selectedTemplateId,
  onSelectTemplate,
  disabled = false,
}: TemplatePickerProps) => {
  const { t } = useTranslation();
  const { data: templates, isLoading } = useQuery(
    convexQuery(api.templates.list, {}),
  );
  const isMobile = useIsMobile();
  const { settings } = useSettings();
  const [filterLanguages, setFilterLanguages] = useState<string[]>(() => [
    ...SUPPORTED_LOCALES,
  ]);
  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!hasInitialized.current && settings) {
      const saved = settings.homepageFilterLanguage;
      setFilterLanguages(
        saved && saved.length > 0 ? saved : [...SUPPORTED_LOCALES],
      );
      hasInitialized.current = true;
    }
  }, [settings]);

  const { mutate: updateSettings } = useMutation({
    mutationFn: useConvexMutation(api.userSettings.updateSettings),
  });

  const filteredTemplates = templates?.filter(
    (template) =>
      filterLanguages.length === 0 ||
      filterLanguages.includes(template.language),
  );

  const handleLanguageToggle = (lang: string) => {
    const newFilterLanguages = filterLanguages.includes(lang)
      ? filterLanguages.filter((l) => l !== lang)
      : [...filterLanguages, lang];

    setFilterLanguages(newFilterLanguages);

    updateSettings(
      { homepageFilterLanguage: newFilterLanguages },
      {
        onError: (error) => {
          console.error(error);
          toast.error(t('errors:errorTogglingLanguage'));
        },
      },
    );
  };

  if (isMobile) {
    return (
      <MobileTemplatePicker
        selectedTemplateId={selectedTemplateId}
        onSelectTemplate={onSelectTemplate}
        disabled={disabled}
        templates={filteredTemplates}
        allTemplates={templates}
        isLoading={isLoading}
        filterLanguages={filterLanguages}
        onToggleLanguage={handleLanguageToggle}
      />
    );
  }

  return (
    <DesktopTemplatePicker
      selectedTemplateId={selectedTemplateId}
      onSelectTemplate={onSelectTemplate}
      disabled={disabled}
      templates={filteredTemplates}
      allTemplates={templates}
      isLoading={isLoading}
      filterLanguages={filterLanguages}
      onToggleLanguage={handleLanguageToggle}
    />
  );
};

interface InnerPickerProps extends TemplatePickerProps {
  templates: Template[] | undefined;
  allTemplates: Template[] | undefined;
  isLoading: boolean;
  filterLanguages: string[];
  onToggleLanguage: (lang: string) => void;
}

const DesktopTemplatePicker = ({
  selectedTemplateId,
  onSelectTemplate,
  disabled,
  templates,
  allTemplates,
  isLoading,
  filterLanguages,
  onToggleLanguage,
}: InnerPickerProps) => {
  const { t } = useTranslation();

  const selectedTemplateObj = allTemplates?.find(
    (tmpl) => tmpl._id === selectedTemplateId,
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-between"
          disabled={disabled || isLoading}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              {t('home:loadingTemplates')}
            </div>
          ) : selectedTemplateObj ? (
            <span className="truncate">
              {selectedTemplateObj.name}
              <span className="text-muted-foreground ml-1 text-xs">
                ({selectedTemplateObj.language})
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              {t('home:selectTemplate')}
            </span>
          )}
          <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-80 p-0">
        <div
          className="bg-background sticky top-0 z-10 border-b px-2 py-2"
          onClick={(e) => e.stopPropagation()}>
          <LanguageFilters
            filterLanguages={filterLanguages}
            onToggleLanguage={onToggleLanguage}
          />
        </div>
        <DropdownMenuRadioGroup
          value={selectedTemplateId ?? ''}
          onValueChange={(value) => onSelectTemplate(value as Id<'templates'>)}>
          {templates?.map((template) => (
            <DropdownMenuRadioItem
              key={template._id}
              value={template._id}
              className="[&_svg]:text-primary! flex flex-col items-start gap-1 py-2.5 [&>span:first-child]:top-1/2 [&>span:first-child]:-translate-y-1/2">
              <span className="flex items-center gap-2 text-sm font-medium">
                {template.name}
                <span className="text-muted-foreground shrink-0 text-xs">
                  {template.language}
                </span>
              </span>
              {template.sections.length > 0 && (
                <span className="text-muted-foreground flex flex-wrap items-center gap-0.5 text-xs">
                  {template.sections.map((section, i) => (
                    <Fragment key={section.title}>
                      <span>{section.title}</span>
                      {i < template.sections.length - 1 && (
                        <Dot className="size-3" />
                      )}
                    </Fragment>
                  ))}
                </span>
              )}
            </DropdownMenuRadioItem>
          ))}
          {templates?.length === 0 && (
            <div className="py-4 text-center text-sm text-gray-500">
              {allTemplates?.length === 0
                ? t('home:noTemplates')
                : t('home:noTemplatesForLanguage')}
            </div>
          )}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const MobileTemplatePicker = ({
  selectedTemplateId,
  onSelectTemplate,
  disabled,
  templates,
  allTemplates,
  isLoading,
  filterLanguages,
  onToggleLanguage,
}: InnerPickerProps) => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const selectedTemplateObj = allTemplates?.find(
    (tmpl) => tmpl._id === selectedTemplateId,
  );

  return (
    <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="outline"
          className="h-12 w-full justify-between"
          disabled={disabled || isLoading}>
          {isLoading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="size-4 animate-spin" />
              {t('home:loadingTemplates')}
            </div>
          ) : selectedTemplateObj ? (
            <span className="truncate">
              {selectedTemplateObj.name}
              <span className="text-muted-foreground ml-1 text-xs">
                ({selectedTemplateObj.language})
              </span>
            </span>
          ) : (
            <span className="text-muted-foreground">
              {t('home:selectTemplate')}
            </span>
          )}
          <ChevronDown className="ml-2 size-4 opacity-50" />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="flex max-h-[80vh] flex-col">
        <div className="mx-auto flex min-h-0 w-full max-w-sm flex-1 flex-col overflow-hidden">
          <DrawerHeader className="shrink-0">
            <DrawerTitle>{t('home:selectTemplate')}</DrawerTitle>
          </DrawerHeader>
          <div className="flex shrink-0 flex-col gap-3 border-b px-4 pb-4">
            <LanguageFilters
              filterLanguages={filterLanguages}
              onToggleLanguage={onToggleLanguage}
            />
          </div>
          <div className="scrollbar-none flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 pb-[calc(2rem+var(--safe-area-bottom,0px))]">
            {templates?.map((template) => (
              <TemplateButton
                key={template._id}
                template={template}
                isSelected={selectedTemplateId === template._id}
                onSelect={() => {
                  onSelectTemplate(template._id);
                  setIsDrawerOpen(false);
                }}
              />
            ))}
            {templates?.length === 0 && (
              <div className="py-8 text-center text-gray-500">
                {allTemplates?.length === 0
                  ? t('home:noTemplates')
                  : t('home:noTemplatesForLanguage')}
              </div>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
